import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import useDbView from '../../../hooks/useDbView';
import logging from '@sstdev/lib_logging';
import useEventSink from '../../../hooks/useEventSink';
import { metadata } from 'lib_ui-services';
import { getRfidEnabledRelations } from '@sstdev/lib_metadata-config';

const _p = { useDbView, getNamespaces: metadata.getNamespaces };
export const _private = _p;

const EMPTY_ARRAY = [];

/**
 * Abstract away the assembly of the recordset that will be given to the dropdown list.
 */

export default function useAssembleRecords(
    hNode,
    filterText = '',
    predefinedRecords,
    consumerReady,
    supportWildCard = false
) {
    // Guard against non-array predefinedRecords
    if (predefinedRecords != null && !Array.isArray(predefinedRecords)) {
        logging.error('useAssembleRecords: predefinedRecords must be an array or null/undefined', {
            receivedType: typeof predefinedRecords,
            value: predefinedRecords
        });
        predefinedRecords = null;
    }

    const [, , request] = useEventSink();
    const [checkedForPredefined, setCheckedForPredefined] = useState(false);
    const [filteredPredefinedRecords, setFilteredPredefinedRecords] = useState(predefinedRecords);
    const [hintRecord, setHintRecord] = useState(EMPTY_ARRAY);
    const {
        id,
        propertyName,
        namespace,
        foreignNamespace: tempForeignNamespace,
        relation,
        foreignRelation: tempForeignRelation,
        dropdownDisplayProperties
    } = hNode || {};

    // Some consumers of this abstract component use namespace/relation instead of
    // foreignNamespace/foreignRelation.
    const foreignNamespace = tempForeignNamespace || namespace;
    const foreignRelation = tempForeignRelation || relation;
    const namespaces = _p.getNamespaces();
    const virtualRecords = useMemo(() => {
        if (foreignNamespace !== 'application' || foreignRelation !== 'rfidEnabledRelation') return null;
        try {
            return getRfidEnabledRelations(namespaces || [], { throwOnInvalid: false });
        } catch (e) {
            logging.error('Failed to get RFID enabled relations', e);
            return [];
        }
    }, [foreignNamespace, foreignRelation, namespaces]);
    if (virtualRecords !== null) predefinedRecords = virtualRecords;

    useEffect(() => {
        // If the dropdown is called directly with predefinedRecords, then we don't need to check for them.
        // This includes EMPTY arrays - they are explicitly provided and don't need fetching
        if (predefinedRecords != null) {
            if (!checkedForPredefined) {
                setCheckedForPredefined(true);
            }
            return;
        }
        const doAsync = async () => {
            const result = await request(
                {},
                { verb: 'get', namespace: 'application', relation: 'predefinedRecords' },
                50,
                false
            );
            setCheckedForPredefined(true);
            if (result?.records) {
                setFilteredPredefinedRecords(result.records);
            }
        };
        consumerReady && doAsync();
    }, [predefinedRecords, checkedForPredefined, request, foreignNamespace, foreignRelation, consumerReady]);
    // We must always call useDbView because of the rules of hooks, but the last parameter of the hook
    // will determine if the hook internals will bother performing database operations that could
    // effect performance.
    const { records, recordCount, viewCriteria, viewReady, localOnlySearchReason } = _p.useDbView(
        foreignNamespace,
        foreignRelation,
        `${foreignNamespace}_${foreignRelation}_${id}`,
        hNode,
        checkedForPredefined && consumerReady && predefinedRecords == null,
        false
    );

    const usingPredefinedRecords = filteredPredefinedRecords != null;
    const effectiveRecordCount = usingPredefinedRecords ? filteredPredefinedRecords.length : recordCount;
    const effectiveViewReady = usingPredefinedRecords ? true : viewReady;

    // Track when a fullTextSearch filter is being applied (async server lookup)
    const [isSearching, setIsSearching] = useState(false);

    const filterGate = useRef({ gate: Promise.resolve(), preCount: 0, postCount: 0 });
    // If the records are from the database, use viewCriteria to filter them.
    useEffect(() => {
        async function doAsync() {
            if (predefinedRecords != null) return;
            // Wait for the view to be ready before applying filters.
            // This prevents applying filters on the empty/placeholder viewCriteria.
            if (!viewReady) return;
            // Queue up the filter changes using a gate so that they cannot overlap.
            // Otherwise, the removeFilters will sometimes be called after but finish before
            // the applyFilters - meaning the filters will be reapplied instead of being removed
            // as intended.
            const release = await createPromiseGate(filterGate);
            try {
                if (filterText === '') {
                    viewCriteria.removeFilters(id + 'ddInput');
                } else {
                    const splitProp = dropdownDisplayProperties?.length > 1 ? { splitChar: ',' } : {};
                    // Show searching indicator while the fullTextSearch filter is being applied
                    setIsSearching(true);
                    await viewCriteria.applyFilters(
                        {
                            // paged filter is a noop for a local lookup, but is needed for an online lookup.
                            paged: { page: 0 },
                            fullTextSearch: {
                                searchTerm: filterText,
                                ...splitProp
                            },
                            propertiesToSearch: { propertiesToSearch: dropdownDisplayProperties || [propertyName] }
                        },
                        id + 'ddInput'
                    );
                    setIsSearching(false);
                }
            } catch (e) {
                logging.error('Error applying filter', e);
                setIsSearching(false);
            } finally {
                release();
            }
        }
        doAsync();
    }, [viewCriteria, viewReady, filterText, id, propertyName, predefinedRecords, dropdownDisplayProperties]);

    const getHintRecord = useCallback(
        (value, hintText) => {
            const prop = (dropdownDisplayProperties || [propertyName])[0];
            return { _id: 'hint-regex', value, [prop]: hintText };
        },
        [propertyName, dropdownDisplayProperties]
    );

    useEffect(() => {
        if (effectiveRecordCount > 0 || !filterText.includes('*') || !supportWildCard) {
            setHintRecord(prev => {
                if (!prev?.length) {
                    return prev;
                }
                return EMPTY_ARRAY;
            });
            return;
        }
        //we got NO records back, the filtertext includes a * AND we are configured to support the wildcard
        if (filterText.endsWith('*')) {
            const hintRecord = getHintRecord(filterText, `Wildcard Filter: ${filterText}`);
            setHintRecord([hintRecord]);
        } else {
            // we only support wildcards at the END of the string
            const hintRecord = getHintRecord('', 'Invalid wild card usage.');
            setHintRecord([hintRecord]);
        }
    }, [effectiveRecordCount, filterText, supportWildCard, usingPredefinedRecords, getHintRecord]);

    // If the records are passed in, then use built-in array filter.
    useEffect(() => {
        if (predefinedRecords == null) return;
        if (filterText === '') {
            setFilteredPredefinedRecords(predefinedRecords);
        } else {
            const filteredRecords = predefinedRecords.filter(r =>
                r[propertyName]?.toLowerCase().includes(filterText.toLowerCase())
            );
            setFilteredPredefinedRecords(filteredRecords);
        }
    }, [predefinedRecords, filterText, propertyName]);

    const predefinedRecordCount = predefinedRecords?.length ?? filteredPredefinedRecords?.length;
    if (hintRecord?.length) {
        return {
            records: hintRecord,
            recordCount: hintRecord.length,
            predefinedRecordCount,
            viewReady: effectiveViewReady,
            currentRecordFilter: filterText,
            isSearching
        };
    }
    if (filteredPredefinedRecords == null) {
        return {
            records,
            recordCount,
            predefinedRecordCount,
            viewReady: effectiveViewReady,
            currentRecordFilter: filterText,
            isSearching,
            localOnlySearchReason
        };
    } else {
        // A predefined list is whatever the caller handed us, in full — there is no database view
        // behind it and so nothing about it can be partial.
        return {
            records: filteredPredefinedRecords,
            recordCount: filteredPredefinedRecords.length,
            predefinedRecordCount,
            viewReady: effectiveViewReady,
            currentRecordFilter: filterText,
            isSearching
        };
    }
}

// Use a promise to gate - you must wait at the gate until the promise from the previous
// call has completed.
async function createPromiseGate(ref) {
    const oldRef = ref.current;
    let release = () => {};
    // Replace the old promise before waiting for it to finish,
    // to make sure a subsequent calls would wait on our new promise,
    // not the same promise we are waiting on.
    // `await` allows other threads to perform logic, but as long as we don't call `await`
    //  JavaScript can be considered atomic.
    ref.current = {
        gate: new Promise(resolve => (release = () => resolve()))
    };
    await oldRef.gate;
    return release;
}
